// 113 — Bitácora UCI Adulto (Fase 4, Tandas 5a+5b+8a, ver PLAN.md). Bloque
// B (antropometría) y Bloque C completo (bitácora, diagnósticos de
// ingreso, constantes vitales + gráfico, ingresos, egresos, reflejos
// miotáticos, escalas, gases, laboratorio, balance hídrico, personal de
// enfermería, ventilación mecánica, procedimientos, oxigenación,
// solicitud de exámenes, cuidados generales, interconsultas, informe de
// enfermería).
import type { JSX } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { AreaDictado } from './AreaDictado';
import { CampoDictado } from './CampoDictado';
import { SelectorDiagnosticoCie10, type DiagnosticoCie10Valor } from './SelectorDiagnosticoCie10';
import {
  CAMPOS_BALANCE_HIDRICO_113,
  CAMPOS_LABORATORIO_113,
  COLUMNAS_INTERCONSULTAS_113,
  COLUMNAS_PROCEDIMIENTOS_OTROS_113,
  FILAS_PROCEDIMIENTOS_OTROS_113,
  TOPE_DRENAJES_113,
  TOPE_OTROS_HORARIOS_113,
  tablasBalance113,
  type GruposNombrables113,
  COLUMNAS_PROCEDIMIENTOS_113,
  COLUMNAS_SOLICITUD_EXAMENES_113,
  FILAS_CONSTANTES_VITALES_113,
  FILAS_EGRESOS_113,
  FILAS_ESCALAS_113,
  FILAS_GASES_113,
  FILAS_INGRESOS_113,
  FILAS_INTERCONSULTAS_113,
  FILAS_PROCEDIMIENTOS_113,
  FILAS_REFLEJOS_113,
  FILAS_SOLICITUD_EXAMENES_113,
  FILAS_VENTILACION_113,
  ITEMS_CUIDADOS_GENERALES_113,
  ITEMS_OXIGENACION_113,
  resumenTotalGlasgowPorHora,
  SERIES_GRAFICO_CONSTANTES_113,
  balanceHidricoEfectivo113,
  CAMPOS_BALANCE_CALCULABLES_113,
  type EstadoAntropometria113,
  type EstadoBalanceHidrico113,
  type EstadoDatosBitacora113,
  type EstadoInformeEnfermeria113,
  type EstadoLaboratorio113,
  type EstadoPersonalEnfermeria113,
} from './bloques113Datos';
import { imcTexto } from './calculosClinicosDatos';
import { diasHospitalizacionUci } from './uci/datosClinicosUci';
import { CamposClinicosUci } from './uci/CamposClinicosUci';
import { FilasNombrablesHorarias } from './uci/FilasNombrablesHorarias';
import { CampoBalanceHidrico } from './uci/CampoBalanceHidrico';
import { TablaHoraria24h, type EstadoTablaHoraria24h } from './uci/TablaHoraria24h';
import { GraficoEnfermeria, type PuntosGraficoEnfermeria } from './uci/GraficoEnfermeria';
import { ChecklistTurno, type EstadoChecklistTurno } from './uci/ChecklistTurno';
import { TablaConColumnas, type EstadoTablaConColumnas } from './uci/TablaConColumnas';

function CampoTexto({ id, label, value, onChange, soloLectura }: { readonly id: string; readonly label: string; readonly value: string; readonly onChange: (v: string) => void; readonly soloLectura?: boolean }): JSX.Element {
  return <CampoDictado id={id} etiqueta={label} valor={value} soloLectura={soloLectura} onChange={onChange} />;
}

function CampoFecha({ id, label, value, onChange }: { readonly id: string; readonly label: string; readonly value: string; readonly onChange: (v: string) => void }): JSX.Element {
  return (
    <div className="flex flex-col gap-1">
      <Label htmlFor={id}>{label}</Label>
      <Input id={id} type="date" value={value} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}

function BloqueAntropometria({ estado, onChange }: { readonly estado: EstadoAntropometria113; readonly onChange: (v: EstadoAntropometria113) => void }): JSX.Element {
  const { t } = useTranslation();
  function actualizar(cambios: Partial<EstadoAntropometria113>): void {
    onChange({ ...estado, ...cambios });
  }
  return (
    <div className="flex flex-col gap-3">
      <h4 className="text-sm font-medium text-hce-ink">{t('formularios.f113.antropometria.titulo')}</h4>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3">
        <CampoTexto id="f113-talla" label={t('formularios.f113.antropometria.talla')} value={estado.talla} onChange={(v) => actualizar({ talla: v })} />
        <CampoTexto id="f113-peso-real" label={t('formularios.f113.antropometria.pesoReal')} value={estado.pesoReal} onChange={(v) => actualizar({ pesoReal: v })} />
        <CampoTexto id="f113-imc" label={t('formularios.f113.antropometria.imc')} value={imcTexto(estado.pesoReal, estado.talla)} onChange={() => undefined} soloLectura />
        <CampoTexto id="f113-peso-ideal" label={t('formularios.f113.antropometria.pesoIdeal')} value={estado.pesoIdeal} onChange={(v) => actualizar({ pesoIdeal: v })} />
        <CampoTexto id="f113-superficie" label={t('formularios.f113.antropometria.superficieCorporal')} value={estado.superficieCorporal} onChange={(v) => actualizar({ superficieCorporal: v })} />
      </div>
    </div>
  );
}

function BloqueDatosBitacora({ estado, onChange }: { readonly estado: EstadoDatosBitacora113; readonly onChange: (v: EstadoDatosBitacora113) => void }): JSX.Element {
  const { t } = useTranslation();
  const diasCalculados = diasHospitalizacionUci(estado.fechaIngresoUci, estado.fechaActual);
  function actualizar(cambios: Partial<EstadoDatosBitacora113>): void {
    onChange({ ...estado, ...cambios });
  }
  return (
    <div className="flex flex-col gap-3">
      <h4 className="text-sm font-medium text-hce-ink">{t('formularios.f113.datosBitacora.titulo')}</h4>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3">
        <CampoTexto id="f113-servicio" label={t('formularios.f113.datosBitacora.servicioProcedencia')} value={estado.servicioProcedencia} onChange={(v) => actualizar({ servicioProcedencia: v })} />
        <CampoFecha id="f113-fecha-ingreso" label={t('formularios.f113.datosBitacora.fechaIngresoUci')} value={estado.fechaIngresoUci} onChange={(v) => actualizar({ fechaIngresoUci: v })} />
        <CampoFecha id="f113-fecha-actual" label={t('formularios.f113.datosBitacora.fechaActual')} value={estado.fechaActual} onChange={(v) => actualizar({ fechaActual: v })} />
        <div className="flex flex-col gap-1">
          <CampoTexto
            id="f113-dias-hosp"
            label={t('formularios.f113.datosBitacora.diasHospitalizacion')}
            value={diasCalculados !== '' ? diasCalculados : estado.diasHospitalizacion}
            soloLectura={diasCalculados !== ''}
            onChange={(v) => actualizar({ diasHospitalizacion: v })}
          />
          <p className="text-xs text-hce-muted">{diasCalculados !== '' ? t('formularios.uci.datosClinicos.diasCalculados') : t('formularios.uci.datosClinicos.diasManual')}</p>
        </div>
        <CamposClinicosUci prefijoId="f113" estado={estado} onChange={(v) => actualizar(v)} />
      </div>
    </div>
  );
}

function BloqueDiagnosticos({ diagnosticos, onChange }: { readonly diagnosticos: readonly DiagnosticoCie10Valor[]; readonly onChange: (v: readonly DiagnosticoCie10Valor[]) => void }): JSX.Element {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-3">
      <h4 className="text-sm font-medium text-hce-ink">{t('formularios.f113.diagnosticosIngresoTitulo')}</h4>
      {diagnosticos.map((d, i) => (
        <SelectorDiagnosticoCie10
          key={i}
          label={t(`formularios.f113.diagnosticosIngreso.${i + 1}`)}
          value={d}
          onChange={(v) => onChange(diagnosticos.map((actual, j) => (j === i ? v : actual)))}
        />
      ))}
    </div>
  );
}

function BloqueLaboratorio({ estado, onChange }: { readonly estado: EstadoLaboratorio113; readonly onChange: (v: EstadoLaboratorio113) => void }): JSX.Element {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-3">
      <h4 className="text-sm font-medium text-hce-ink">{t('formularios.f113.laboratorioTitulo')}</h4>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-6">
        {CAMPOS_LABORATORIO_113.map((c) => (
          <CampoTexto key={c} id={`f113-lab-${c}`} label={t(`formularios.f113.laboratorio.${c}`)} value={estado[c]} onChange={(v) => onChange({ ...estado, [c]: v })} />
        ))}
      </div>
    </div>
  );
}

function BloqueGraficoConstantes({ puntos, onChange }: { readonly puntos: PuntosGraficoEnfermeria; readonly onChange: (v: PuntosGraficoEnfermeria) => void }): JSX.Element {
  const { t } = useTranslation();
  const [serieActiva, setSerieActiva] = useState(SERIES_GRAFICO_CONSTANTES_113[0]!.clave);
  const [varianteActiva, setVarianteActiva] = useState<string | undefined>(SERIES_GRAFICO_CONSTANTES_113[0]!.variantes?.[0]?.clave);
  return (
    <div className="flex flex-col gap-2">
      <h4 className="text-sm font-medium text-hce-ink">{t('formularios.f113.graficoConstantesTitulo')}</h4>
      <GraficoEnfermeria
        series={SERIES_GRAFICO_CONSTANTES_113}
        puntos={puntos}
        onChange={onChange}
        serieActiva={serieActiva}
        onChangeSerieActiva={(clave) => {
          setSerieActiva(clave);
          setVarianteActiva(SERIES_GRAFICO_CONSTANTES_113.find((s) => s.clave === clave)?.variantes?.[0]?.clave);
        }}
        varianteActiva={varianteActiva}
        onChangeVarianteActiva={setVarianteActiva}
      />
    </div>
  );
}

function BloqueBalanceHidrico({ estado, ingresos, egresos, onChange }: { readonly estado: EstadoBalanceHidrico113; readonly ingresos: EstadoTablaHoraria24h; readonly egresos: EstadoTablaHoraria24h; readonly onChange: (v: EstadoBalanceHidrico113) => void }): JSX.Element {
  // `ingresos`/`egresos` ya traen los renglones nombrables sumados (ver `tablasBalance113`).
  const { t } = useTranslation();
  const efectivo = balanceHidricoEfectivo113(estado, ingresos, egresos);
  return (
    <div className="flex flex-col gap-3">
      <h4 className="text-sm font-medium text-hce-ink">{t('formularios.f113.balanceHidricoTitulo')}</h4>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-5">
        {CAMPOS_BALANCE_HIDRICO_113.map((c) => (
          <CampoBalanceHidrico
            key={c}
            id={`f113-balance-${c}`}
            label={t(`formularios.f113.balanceHidrico.${c}`)}
            manual={estado[c]}
            efectivo={efectivo[c]}
            calculable={CAMPOS_BALANCE_CALCULABLES_113.includes(c)}
            onChange={(v) => onChange({ ...estado, [c]: v })}
          />
        ))}
      </div>
    </div>
  );
}

function BloquePersonalEnfermeria({ estado, onChange }: { readonly estado: EstadoPersonalEnfermeria113; readonly onChange: (v: EstadoPersonalEnfermeria113) => void }): JSX.Element {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-3">
      <h4 className="text-sm font-medium text-hce-ink">{t('formularios.f113.personalEnfermeriaTitulo')}</h4>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <CampoTexto id="f113-personal-am" label={t('formularios.f113.personalEnfermeria.am')} value={estado.am} onChange={(v) => onChange({ ...estado, am: v })} />
        <CampoTexto id="f113-personal-pm" label={t('formularios.f113.personalEnfermeria.pm')} value={estado.pm} onChange={(v) => onChange({ ...estado, pm: v })} />
        <CampoTexto id="f113-personal-hs" label={t('formularios.f113.personalEnfermeria.hs')} value={estado.hs} onChange={(v) => onChange({ ...estado, hs: v })} />
      </div>
    </div>
  );
}

function BloqueInformeEnfermeria({ estado, onChange }: { readonly estado: EstadoInformeEnfermeria113; readonly onChange: (v: EstadoInformeEnfermeria113) => void }): JSX.Element {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-3">
      <h4 className="text-sm font-medium text-hce-ink">{t('formularios.f113.informeEnfermeriaTitulo')}</h4>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        {(['am', 'pm', 'hs'] as const).map((turno) => (
          <AreaDictado
            key={turno}
            id={`f113-informe-${turno}`}
            etiqueta={t(`formularios.f113.informeEnfermeria.${turno}`)}
            valor={estado[turno]}
            onChange={(v) => onChange({ ...estado, [turno]: v })}
            filas={8}
          />
        ))}
      </div>
      <p className="text-xs text-hce-muted">{t('formularios.uci.informe.ayuda')}</p>
    </div>
  );
}

export interface Bloques113Props {
  readonly antropometria: EstadoAntropometria113;
  readonly onChangeAntropometria: (v: EstadoAntropometria113) => void;
  readonly datosBitacora: EstadoDatosBitacora113;
  readonly onChangeDatosBitacora: (v: EstadoDatosBitacora113) => void;
  readonly diagnosticos: readonly DiagnosticoCie10Valor[];
  readonly onChangeDiagnosticos: (v: readonly DiagnosticoCie10Valor[]) => void;
  readonly constantesVitales: EstadoTablaHoraria24h;
  readonly onChangeConstantesVitales: (v: EstadoTablaHoraria24h) => void;
  readonly graficoConstantes: PuntosGraficoEnfermeria;
  readonly onChangeGraficoConstantes: (v: PuntosGraficoEnfermeria) => void;
  readonly ingresos: EstadoTablaHoraria24h;
  readonly onChangeIngresos: (v: EstadoTablaHoraria24h) => void;
  readonly egresos: EstadoTablaHoraria24h;
  readonly onChangeEgresos: (v: EstadoTablaHoraria24h) => void;
  readonly reflejos: EstadoTablaHoraria24h;
  readonly onChangeReflejos: (v: EstadoTablaHoraria24h) => void;
  readonly escalas: EstadoTablaHoraria24h;
  readonly onChangeEscalas: (v: EstadoTablaHoraria24h) => void;
  readonly gases: EstadoTablaHoraria24h;
  readonly onChangeGases: (v: EstadoTablaHoraria24h) => void;
  readonly laboratorio: EstadoLaboratorio113;
  readonly onChangeLaboratorio: (v: EstadoLaboratorio113) => void;
  readonly balanceHidrico: EstadoBalanceHidrico113;
  readonly onChangeBalanceHidrico: (v: EstadoBalanceHidrico113) => void;
  readonly personalEnfermeria: EstadoPersonalEnfermeria113;
  readonly onChangePersonalEnfermeria: (v: EstadoPersonalEnfermeria113) => void;
  readonly ventilacion: EstadoTablaHoraria24h;
  readonly onChangeVentilacion: (v: EstadoTablaHoraria24h) => void;
  readonly procedimientos: EstadoTablaConColumnas;
  readonly onChangeProcedimientos: (v: EstadoTablaConColumnas) => void;
  readonly oxigenacion: EstadoChecklistTurno;
  readonly onChangeOxigenacion: (v: EstadoChecklistTurno) => void;
  readonly solicitudExamenes: EstadoTablaConColumnas;
  readonly onChangeSolicitudExamenes: (v: EstadoTablaConColumnas) => void;
  readonly cuidadosGenerales: EstadoChecklistTurno;
  readonly onChangeCuidadosGenerales: (v: EstadoChecklistTurno) => void;
  readonly interconsultas: EstadoTablaConColumnas;
  readonly onChangeInterconsultas: (v: EstadoTablaConColumnas) => void;
  readonly informeEnfermeria: EstadoInformeEnfermeria113;
  readonly onChangeInformeEnfermeria: (v: EstadoInformeEnfermeria113) => void;
  readonly grupos: GruposNombrables113;
  readonly onChangeGrupos: (v: GruposNombrables113) => void;
  readonly procedimientosOtros: EstadoTablaConColumnas;
  readonly onChangeProcedimientosOtros: (v: EstadoTablaConColumnas) => void;
}

export function Bloques113({
  antropometria,
  onChangeAntropometria,
  datosBitacora,
  onChangeDatosBitacora,
  diagnosticos,
  onChangeDiagnosticos,
  constantesVitales,
  onChangeConstantesVitales,
  graficoConstantes,
  onChangeGraficoConstantes,
  ingresos,
  onChangeIngresos,
  egresos,
  onChangeEgresos,
  reflejos,
  onChangeReflejos,
  escalas,
  onChangeEscalas,
  gases,
  onChangeGases,
  laboratorio,
  onChangeLaboratorio,
  balanceHidrico,
  onChangeBalanceHidrico,
  personalEnfermeria,
  onChangePersonalEnfermeria,
  ventilacion,
  onChangeVentilacion,
  procedimientos,
  onChangeProcedimientos,
  oxigenacion,
  onChangeOxigenacion,
  solicitudExamenes,
  onChangeSolicitudExamenes,
  cuidadosGenerales,
  onChangeCuidadosGenerales,
  interconsultas,
  onChangeInterconsultas,
  informeEnfermeria,
  onChangeInformeEnfermeria,
  grupos,
  onChangeGrupos,
  procedimientosOtros,
  onChangeProcedimientosOtros,
}: Bloques113Props): JSX.Element {
  const { t } = useTranslation();
  const tablasBalance = tablasBalance113({ ingresos, egresos, grupos });
  const resumenGlasgow = resumenTotalGlasgowPorHora(escalas);
  return (
    <div className="flex flex-col gap-6">
      <BloqueAntropometria estado={antropometria} onChange={onChangeAntropometria} />
      <BloqueDatosBitacora estado={datosBitacora} onChange={onChangeDatosBitacora} />
      <BloqueDiagnosticos diagnosticos={diagnosticos} onChange={onChangeDiagnosticos} />

      <div className="flex flex-col gap-2">
        <h4 className="text-sm font-medium text-hce-ink">{t('formularios.f113.constantesVitalesTitulo')}</h4>
        <TablaHoraria24h filas={FILAS_CONSTANTES_VITALES_113} estado={constantesVitales} onChange={onChangeConstantesVitales} />
        <h5 className="text-xs font-medium text-hce-muted">{t('formularios.f113.otrasConstantesTitulo')}</h5>
        <FilasNombrablesHorarias id="f113-otras-constantes" estado={grupos.otrasConstantes} tope={TOPE_OTROS_HORARIOS_113} onChange={(v) => onChangeGrupos({ ...grupos, otrasConstantes: v })} />
      </div>

      <BloqueGraficoConstantes puntos={graficoConstantes} onChange={onChangeGraficoConstantes} />

      <div className="flex flex-col gap-2">
        <h4 className="text-sm font-medium text-hce-ink">{t('formularios.f113.ingresosTitulo')}</h4>
        <TablaHoraria24h filas={FILAS_INGRESOS_113} estado={ingresos} onChange={onChangeIngresos} />
        <h5 className="text-xs font-medium text-hce-muted">{t('formularios.f113.medicacionTitulo')}</h5>
        <FilasNombrablesHorarias id="f113-medicacion" unidad="mL" estado={grupos.medicacion} onChange={(v) => onChangeGrupos({ ...grupos, medicacion: v })} />
        <h5 className="text-xs font-medium text-hce-muted">{t('formularios.f113.infusionesTitulo')}</h5>
        <FilasNombrablesHorarias id="f113-infusiones" unidad="mL" estado={grupos.infusiones} onChange={(v) => onChangeGrupos({ ...grupos, infusiones: v })} />
      </div>

      <div className="flex flex-col gap-2">
        <h4 className="text-sm font-medium text-hce-ink">{t('formularios.f113.egresosTitulo')}</h4>
        <TablaHoraria24h filas={FILAS_EGRESOS_113} estado={egresos} onChange={onChangeEgresos} />
        <h5 className="text-xs font-medium text-hce-muted">{t('formularios.f113.drenajesTitulo')}</h5>
        <FilasNombrablesHorarias id="f113-drenajes" unidad="mL" estado={grupos.drenajes} tope={TOPE_DRENAJES_113} onChange={(v) => onChangeGrupos({ ...grupos, drenajes: v })} />
      </div>

      <div className="flex flex-col gap-2">
        <h4 className="text-sm font-medium text-hce-ink">{t('formularios.f113.reflejosTitulo')}</h4>
        <TablaHoraria24h filas={FILAS_REFLEJOS_113} estado={reflejos} onChange={onChangeReflejos} />
      </div>

      <div className="flex flex-col gap-2">
        <h4 className="text-sm font-medium text-hce-ink">{t('formularios.f113.escalasTitulo')}</h4>
        <TablaHoraria24h filas={FILAS_ESCALAS_113} estado={escalas} onChange={onChangeEscalas} />
        <p className="text-xs text-hce-muted">
          {t('formularios.f113.escalas.totalGlasgow')}: {resumenGlasgow || '—'}
        </p>
      </div>

      <div className="flex flex-col gap-2">
        <h4 className="text-sm font-medium text-hce-ink">{t('formularios.f113.gasesTitulo')}</h4>
        <TablaHoraria24h filas={FILAS_GASES_113} estado={gases} onChange={onChangeGases} />
      </div>

      <BloqueLaboratorio estado={laboratorio} onChange={onChangeLaboratorio} />
      <BloqueBalanceHidrico estado={balanceHidrico} ingresos={tablasBalance.ingresos} egresos={tablasBalance.egresos} onChange={onChangeBalanceHidrico} />
      <BloquePersonalEnfermeria estado={personalEnfermeria} onChange={onChangePersonalEnfermeria} />

      <div className="flex flex-col gap-2">
        <h4 className="text-sm font-medium text-hce-ink">{t('formularios.f113.ventilacionTitulo')}</h4>
        <TablaHoraria24h filas={FILAS_VENTILACION_113} estado={ventilacion} onChange={onChangeVentilacion} />
        <h5 className="text-xs font-medium text-hce-muted">{t('formularios.f113.otrosVentilacionTitulo')}</h5>
        <FilasNombrablesHorarias id="f113-otros-ventilacion" estado={grupos.otrosVentilacion} tope={TOPE_OTROS_HORARIOS_113} onChange={(v) => onChangeGrupos({ ...grupos, otrosVentilacion: v })} />
      </div>

      <div className="flex flex-col gap-2">
        <h4 className="text-sm font-medium text-hce-ink">{t('formularios.f113.procedimientosTitulo')}</h4>
        <TablaConColumnas filas={FILAS_PROCEDIMIENTOS_113} columnas={COLUMNAS_PROCEDIMIENTOS_113} estado={procedimientos} onChange={onChangeProcedimientos} />
        <h5 className="text-xs font-medium text-hce-muted">{t('formularios.f113.procedimientosOtrosTitulo')}</h5>
        <TablaConColumnas filas={FILAS_PROCEDIMIENTOS_OTROS_113} columnas={COLUMNAS_PROCEDIMIENTOS_OTROS_113} estado={procedimientosOtros} onChange={onChangeProcedimientosOtros} />
      </div>

      <div className="flex flex-col gap-2">
        <h4 className="text-sm font-medium text-hce-ink">{t('formularios.f113.oxigenacionTitulo')}</h4>
        <ChecklistTurno items={ITEMS_OXIGENACION_113} estado={oxigenacion} onChange={onChangeOxigenacion} />
      </div>

      <div className="flex flex-col gap-2">
        <h4 className="text-sm font-medium text-hce-ink">{t('formularios.f113.solicitudExamenesTitulo')}</h4>
        <TablaConColumnas filas={FILAS_SOLICITUD_EXAMENES_113} columnas={COLUMNAS_SOLICITUD_EXAMENES_113} estado={solicitudExamenes} onChange={onChangeSolicitudExamenes} />
      </div>

      <div className="flex flex-col gap-2">
        <h4 className="text-sm font-medium text-hce-ink">{t('formularios.f113.cuidadosGeneralesTitulo')}</h4>
        <ChecklistTurno items={ITEMS_CUIDADOS_GENERALES_113} estado={cuidadosGenerales} onChange={onChangeCuidadosGenerales} />
      </div>

      <div className="flex flex-col gap-2">
        <h4 className="text-sm font-medium text-hce-ink">{t('formularios.f113.interconsultasTitulo')}</h4>
        <TablaConColumnas filas={FILAS_INTERCONSULTAS_113} columnas={COLUMNAS_INTERCONSULTAS_113} estado={interconsultas} onChange={onChangeInterconsultas} />
      </div>

      <BloqueInformeEnfermeria estado={informeEnfermeria} onChange={onChangeInformeEnfermeria} />
    </div>
  );
}
